import React, { useRef, useState } from "react";
import type { SyntrofiUser } from "../syntrofi/auth";

interface Props {
  title: string;
  dirty: boolean;
  savedAt: Date | null;
  saveType: "manual" | "auto" | null;
  canRename: boolean;
  theme: "dark" | "light";
  onRename: (name: string) => Promise<void>;
  onSave: () => void;
  currentUser: SyntrofiUser | null;
  isGuestBoard: boolean;
  onSaveCopy: () => void;
  onLogout: () => void;
}

export const BoardStatusBar: React.FC<Props> = ({
  title,
  dirty,
  savedAt,
  saveType,
  canRename,
  theme,
  onRename,
  onSave,
  currentUser,
  isGuestBoard,
  onSaveCopy,
  onLogout,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const statusText = () => {
    if (!currentUser) return "not logged in";
    if (isGuestBoard) return "viewing (guest)";
    if (dirty || !savedAt) return "unsaved";
    const hh = savedAt.getHours().toString().padStart(2, "0");
    const mm = savedAt.getMinutes().toString().padStart(2, "0");
    return saveType === "auto" ? `autosaved ${hh}:${mm}` : `saved ${hh}:${mm}`;
  };

  const isDark = theme === "dark";
  const bg = isDark ? "rgba(30, 30, 46, 0.88)" : "rgba(255, 255, 255, 0.92)";
  const fg = isDark ? "#cdd6f4" : "#1e1e2e";
  const border = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.1)";
  const statusColor =
    !currentUser || isGuestBoard
      ? isDark ? "#89b4fa" : "#4a90d9"
      : dirty || !savedAt
        ? isDark ? "#f38ba8" : "#c0392b"
        : isDark ? "#a6e3a1" : "#2d8a4e";

  const startEdit = () => {
    setDraft(title);
    setEditing(true);
  };

  const confirmEdit = async () => {
    const name = draft.trim() || title;
    setEditing(false);
    if (name !== title) { await onRename(name); }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { confirmEdit(); }
    if (e.key === "Escape") { setEditing(false); }
  };

  return (
    <>
      {/* Board title + save status */}
      <div
        style={{
          position: "fixed",
          top: 20,
          left: "calc(26px + 12.5vw)",
          transform: "translateX(-50%)",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 36,
          background: bg,
          border,
          backdropFilter: "blur(4px)",
          borderRadius: 8,
          padding: "0 12px",
          color: fg,
          pointerEvents: "auto",
          userSelect: "none",
          maxWidth: "calc(25vw - 80px)",
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={confirmEdit}
            onKeyDown={onKeyDown}
            autoFocus
            style={{
              background: "transparent",
              border: "none",
              borderBottom: `1px solid ${isDark ? "#89b4fa" : "#4a90d9"}`,
              color: fg,
              fontSize: 15,
              fontWeight: 500,
              outline: "none",
              width: Math.max(80, draft.length * 9),
              maxWidth: 300,
              padding: "0 2px",
            }}
          />
        ) : (
          <span
            onClick={canRename ? startEdit : undefined}
            title={canRename ? "Click to rename" : undefined}
            style={{
              fontSize: 15,
              fontWeight: 500,
              cursor: canRename ? "text" : "default",
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              opacity: canRename ? 1 : 0.6,
            }}
          >
            {title}
          </span>
        )}
        <span
          style={{
            color: statusColor,
            fontSize: 11,
            whiteSpace: "nowrap",
            flexShrink: 0,
            paddingTop: 1,
          }}
        >
          · {statusText()}
        </span>
        {/* Save / Save copy button */}
        {currentUser && (
          <button
            onClick={isGuestBoard ? onSaveCopy : onSave}
            title={isGuestBoard ? "Save a copy to your account" : "Save now"}
            style={{
              background: isGuestBoard ? (isDark ? "#89b4fa" : "#4a90d9") : "none",
              border: isGuestBoard ? "none" : "none",
              borderRadius: isGuestBoard ? 4 : 0,
              cursor: "pointer",
              padding: isGuestBoard ? "2px 6px" : "0 0 0 2px",
              color: isGuestBoard
                ? "#1e1e2e"
                : dirty || !savedAt
                  ? statusColor
                  : isDark ? "rgba(205,214,244,0.25)" : "rgba(30,30,46,0.25)",
              fontSize: isGuestBoard ? 11 : undefined,
              fontWeight: isGuestBoard ? 600 : undefined,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              transition: "color 0.2s",
            }}
          >
            {isGuestBoard ? "Save copy" : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Auth widget - top right corner */}
      <div
        style={{
          position: "fixed",
          top: 20,
          right: 16,
          zIndex: 10,
          pointerEvents: "auto",
        }}
      >
        {currentUser ? (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              title={`Logged in as @${currentUser.login}`}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: fg,
              }}
            >
              {currentUser.avatar_url ? (
                <img
                  src={currentUser.avatar_url}
                  alt={currentUser.login}
                  style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${border}` }}
                />
              ) : (
                <div
                  style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: isDark ? "#313244" : "#e0e0e0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 600, color: fg,
                  }}
                >
                  {currentUser.login[0].toUpperCase()}
                </div>
              )}
            </button>
            {showUserMenu && (
              <div
                style={{
                  position: "absolute",
                  top: 36,
                  right: 0,
                  background: isDark ? "#1e1e2e" : "#fff",
                  border: isDark ? "1px solid #313244" : "1px solid #ddd",
                  borderRadius: 8,
                  padding: "8px 0",
                  minWidth: 160,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  color: fg,
                }}
              >
                <div style={{ padding: "6px 14px 10px", borderBottom: isDark ? "1px solid #313244" : "1px solid #eee" }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{currentUser.name || currentUser.login}</div>
                  <div style={{ fontSize: 12, color: isDark ? "#6c7086" : "#888" }}>@{currentUser.login}</div>
                </div>
                <button
                  onClick={() => { setShowUserMenu(false); onLogout(); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "none", border: "none", padding: "8px 14px",
                    cursor: "pointer", color: fg, fontSize: 14,
                  }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          <a
            href="/auth/login"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: isDark ? "#313244" : "#f5f5f5",
              border: isDark ? "1px solid #45475a" : "1px solid #ddd",
              borderRadius: 8,
              color: fg,
              fontSize: 13,
              fontWeight: 500,
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
            </svg>
            Login with GitHub
          </a>
        )}
      </div>
    </>
  );
};
